import type {
  CreditNoticeContext,
  CreditNoticeEvent,
  CreditNoticeState,
} from './types';
import type { I18nLanguage } from '@/i18n/const';
import { SUPPORTED_I18N_LANGUAGES } from '@/i18n/const';
import { CREDIT_NOTICE_EVENT_STATUSES } from './types';

const CREDIT_NOTICE_STATUSES: ReadonlySet<string> = new Set(CREDIT_NOTICE_EVENT_STATUSES);

export function shouldReleaseCreditNoticePriority(
  previousPoints: number | undefined,
  nextPoints: number,
): boolean {
  return previousPoints === 0 && nextPoints > 0;
}

interface CreditNoticeHistoryContext {
  projectId: string;
  sessionId: string;
  messageId: string;
  receivedAt: number;
}

export interface CreditNoticeParseDiagnostics {
  projectId?: string;
  sessionId?: string;
  noticeId?: string;
  noticeStatus?: string;
  panelLanguage?: string;
  missingFields: string[];
  payloadShape: string;
}

export interface CreditNoticeParseResult {
  event: CreditNoticeEvent | null;
  diagnostics: CreditNoticeParseDiagnostics;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getRequiredString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function parsePanelLanguage(value: unknown): I18nLanguage | null {
  return typeof value === 'string'
    && SUPPORTED_I18N_LANGUAGES.has(value)
    ? value as I18nLanguage
    : null;
}

function isCreditNoticeEventStatus(value: unknown): value is CreditNoticeEvent['status'] {
  return typeof value === 'string' && CREDIT_NOTICE_STATUSES.has(value);
}

function parseNoticeStatus(value: unknown): CreditNoticeEvent['status'] | null {
  return isCreditNoticeEventStatus(value) ? value : null;
}

function getDiagnosticEnum(value: unknown, allowedValues: ReadonlySet<string>): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  return allowedValues.has(value) ? value : 'invalid';
}

function parseNextSteps(value: unknown): CreditNoticeEvent['nextSteps'] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const nextSteps = value.map((item) => {
    if (!isRecord(item)) {
      return null;
    }

    const title = getRequiredString(item, 'title');
    const description = getRequiredString(item, 'description');
    const step = item.step;
    if (!title || !description || typeof step !== 'number' || !Number.isFinite(step)) {
      return null;
    }

    return { step, title, description };
  });

  return nextSteps.every(step => step !== null) ? nextSteps : null;
}

export function getCreditNoticeSummary(input: unknown): string | null {
  if (!isRecord(input) || !Array.isArray(input.content)) {
    return null;
  }

  const summary = input.content.find((item) => (
    isRecord(item)
    && item.type === 'output_text'
    && typeof item.text === 'string'
    && item.text.trim()
  ));

  return isRecord(summary) && typeof summary.text === 'string' && summary.text.trim()
    ? summary.text
    : null;
}

export function isCreditNoticeHistoryItem(input: unknown): input is Record<string, unknown> {
  return isRecord(input) && input._messageType === 'credit_notice';
}

export function parseCreditNoticeHistoryItem(
  input: unknown,
  context: CreditNoticeHistoryContext,
): CreditNoticeEvent | null {
  return parseCreditNoticeHistoryItemWithDiagnostics(input, context).event;
}

export function createInitialCreditNoticeState(): CreditNoticeState {
  return {
    status: 'idle',
    event: null,
    hasPriority: false,
    ignoredMessageId: null,
    summaryReveal: null,
  };
}

export function parseCreditNoticeSocketEvent(input: unknown): CreditNoticeEvent | null {
  return parseCreditNoticeSocketEventWithDiagnostics(input).event;
}

export function parseCreditNoticeHistoryItemWithDiagnostics(
  input: unknown,
  context: CreditNoticeHistoryContext,
): CreditNoticeParseResult {
  const item = isRecord(input) ? input : {};
  const noticeId = getRequiredString(item, '_noticeId');
  const status = parseNoticeStatus(item._noticeStatus);
  const goal = getRequiredString(item, '_noticeGoal');
  const suggestion = getRequiredString(item, '_noticeSuggestion');
  const nextSteps = parseNextSteps(item._noticeNextSteps);
  const panelLanguage = parsePanelLanguage(item._noticePanelLanguage);
  const summary = getCreditNoticeSummary(input);
  const diagnosticStatus = getDiagnosticEnum(item._noticeStatus, CREDIT_NOTICE_STATUSES);
  const diagnosticPanelLanguage = getDiagnosticEnum(item._noticePanelLanguage, SUPPORTED_I18N_LANGUAGES);
  const missingFields: string[] = [];

  if (!isCreditNoticeHistoryItem(input)) {
    missingFields.push('messageType');
  }
  if (!context.projectId) {
    missingFields.push('projectId');
  }
  if (!context.sessionId) {
    missingFields.push('sessionId');
  }
  if (!context.messageId) {
    missingFields.push('messageId');
  }
  if (!Number.isFinite(context.receivedAt)) {
    missingFields.push('timestamp');
  }
  if (!noticeId) {
    missingFields.push('noticeId');
  }
  if (!status) {
    missingFields.push('status');
  }
  if (status === 'completed') {
    if (!summary) {
      missingFields.push('summary');
    }
    if (!goal) {
      missingFields.push('goal');
    }
    if (!suggestion) {
      missingFields.push('suggestion');
    }
    if (!nextSteps) {
      missingFields.push('nextSteps');
    }
    if (!panelLanguage) {
      missingFields.push('panelLanguage');
    }
  }

  const diagnostics: CreditNoticeParseDiagnostics = {
    ...(noticeId ? { noticeId } : {}),
    ...(diagnosticStatus ? { noticeStatus: diagnosticStatus } : {}),
    ...(diagnosticPanelLanguage ? { panelLanguage: diagnosticPanelLanguage } : {}),
    missingFields,
    payloadShape: `history:${isRecord(input) ? 'item-object' : 'item-invalid'}`,
  };
  if (missingFields.length > 0 || !noticeId || !status) {
    return { event: null, diagnostics };
  }

  const baseEvent: CreditNoticeEvent = {
    projectId: context.projectId,
    sessionId: context.sessionId,
    noticeId,
    messageId: context.messageId,
    status,
    receivedAt: context.receivedAt,
  };
  if (status !== 'completed') {
    return { event: baseEvent, diagnostics };
  }
  if (!summary || !goal || !suggestion || !nextSteps || !panelLanguage) {
    return { event: null, diagnostics };
  }
  return {
    event: { ...baseEvent, summary, goal, suggestion, nextSteps, panelLanguage },
    diagnostics,
  };
}

export function parseCreditNoticeSocketEventWithDiagnostics(input: unknown): CreditNoticeParseResult {
  const envelope = isRecord(input) ? input : {};
  const data = isRecord(envelope.data) ? envelope.data : {};
  const projectId = getRequiredString(data, 'projectId');
  const sessionId = getRequiredString(data, 'sessionId');
  const messageId = getRequiredString(data, 'messageId');
  const noticeId = getRequiredString(data, 'noticeId') ?? messageId;
  const status = parseNoticeStatus(data.status);
  const receivedAt = typeof envelope.timestamp === 'number' ? envelope.timestamp : data.timestamp;
  const goal = getRequiredString(data, 'goal');
  const suggestion = getRequiredString(data, 'suggestion');
  const summary = getRequiredString(data, 'summary');
  const nextSteps = parseNextSteps(data.nextSteps);
  const panelLanguage = parsePanelLanguage(data.panelLanguage);
  const diagnosticStatus = getDiagnosticEnum(data.status, CREDIT_NOTICE_STATUSES);
  const diagnosticPanelLanguage = getDiagnosticEnum(data.panelLanguage, SUPPORTED_I18N_LANGUAGES);
  const missingFields: string[] = [];

  if (!projectId) {
    missingFields.push('projectId');
  }
  if (!sessionId) {
    missingFields.push('sessionId');
  }
  if (!messageId) {
    missingFields.push('messageId');
  }
  if (!status) {
    missingFields.push('status');
  }
  if (typeof receivedAt !== 'number' || !Number.isFinite(receivedAt)) {
    missingFields.push('timestamp');
  }
  if (status === 'completed') {
    if (!summary) {
      missingFields.push('summary');
    }
    if (!goal) {
      missingFields.push('goal');
    }
    if (!suggestion) {
      missingFields.push('suggestion');
    }
    if (!nextSteps) {
      missingFields.push('nextSteps');
    }
    if (!panelLanguage) {
      missingFields.push('panelLanguage');
    }
  }
  const diagnostics: CreditNoticeParseDiagnostics = {
    ...(projectId ? { projectId } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(noticeId ? { noticeId } : {}),
    ...(diagnosticStatus ? { noticeStatus: diagnosticStatus } : {}),
    ...(diagnosticPanelLanguage ? { panelLanguage: diagnosticPanelLanguage } : {}),
    missingFields,
    payloadShape: `envelope:${isRecord(envelope.data) ? 'data-object' : 'data-invalid'}`,
  };
  if (
    missingFields.length > 0
    || !projectId
    || !sessionId
    || !noticeId
    || !messageId
    || !status
    || typeof receivedAt !== 'number'
  ) {
    return { event: null, diagnostics };
  }

  const baseEvent: CreditNoticeEvent = {
    projectId,
    sessionId,
    noticeId,
    messageId,
    status,
    receivedAt,
  };
  if (status !== 'completed') {
    return { event: baseEvent, diagnostics };
  }
  if (!summary || !goal || !suggestion || !nextSteps || !panelLanguage) {
    return { event: null, diagnostics };
  }
  return {
    event: {
      ...baseEvent,
      summary,
      goal,
      suggestion,
      nextSteps,
      panelLanguage,
    },
    diagnostics,
  };
}

export function reduceCreditNoticeState(
  state: CreditNoticeState,
  event: CreditNoticeEvent | null,
  context: CreditNoticeContext,
  options: { deferSummary?: boolean } = {},
): CreditNoticeState {
  if (!event || !context.isFreeUser || event.projectId !== context.currentProjectId) {
    return state;
  }

  if (event.noticeId === state.ignoredMessageId) {
    return state;
  }

  const enrichesCompletedNotice = Boolean(
    state.event
    && state.event.noticeId === event.noticeId
    && state.event.status === 'completed'
    && event.status === 'completed'
    && event.summary
    && !state.event.summary,
  );

  if (state.event && event.receivedAt <= state.event.receivedAt && !enrichesCompletedNotice) {
    return state;
  }

  if (
    state.event?.noticeId === event.noticeId
    && (state.status === 'completed' || state.status === 'failed')
    && !enrichesCompletedNotice
  ) {
    return state;
  }

  return {
    status: event.status,
    event: enrichesCompletedNotice && state.event
      ? { ...event, receivedAt: Math.max(event.receivedAt, state.event.receivedAt) }
      : event,
    hasPriority: event.status === 'in_progress' || event.status === 'completed',
    ignoredMessageId: state.ignoredMessageId,
    summaryReveal: options.deferSummary && event.status === 'completed'
      ? { projectId: event.projectId, noticeId: event.noticeId }
      : state.summaryReveal,
  };
}
